import React, {useEffect, useMemo, useState} from 'react';
import {useTheme} from "@mui/material/styles";
import {useTranslation} from "react-i18next";
import {MaterialReactTable, MRT_ActionMenuItem, MRT_ColumnDef, useMaterialReactTable} from "material-react-table";
import {Delete, Edit} from "@mui/icons-material";
import {Avatar, Box} from "@mui/material";
import Grid from '@mui/material/Grid';
import AddIcon from "@mui/icons-material/Add";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import {
    getMuiTableBodyCellProps,
    getMuiTableBodyRowProps,
    getMuiTableHeadCellProps,
    getMuiTablePaperProps
} from "../../../../ui/mui/mui_react_table/MuiReactTableStyles";
import MuiReadOnlyBoxBody from "../../../../ui/mui/mui-display-data/MuiReadOnlyBoxBody";
import MRTMuiButton from "../../../../ui/mui/mui_react_table/MRTMuiButton";
import Loading from "../../../../ui/Loading";
import MuiFabButtonBack from "../../../../ui/mui/mui_buttons/fab/extended/MuiFabButtonBack";
import MuiAlertDialog from "../../../../ui/mui/mui_modal/MuiAlertDialog";
import MuiFetchingSpinner from "../../../../ui/mui/MuiFetchingSpinner";
import MuiModal from "../../../../ui/mui/mui_modal/MuiModal";
import useApiDeleteDescription from "./api-hooks/useApiDeleteDescription";
import useApiGetDescriptions from "./api-hooks/useApiGetDescriptions";
import useCustomCellRenderer from "./useCustomCellRenderer";
import {MRT_Localization_IT} from "material-react-table/locales/it";
import DescriptionForm from "./DescriptionForm";
import {MdOutlineWbIncandescent} from "react-icons/md";
import {IDescription} from "../../../../../utils/interfaces/service/description/IDescription";


interface Props {
    onSelect?: (obj: IDescription | undefined) => void,
    onClose?: () => void;
    scope: string;
}


const DescriptionTable = ({onSelect, onClose, scope}: Props) => {
    const theme = useTheme();
    const {t} = useTranslation()

    const [selectedRow, setSelectedRow] = useState<IDescription | undefined>()
    const [rows, setRows] = useState<IDescription[] | undefined>()

    const [isEditFormOpen, setIsEditFormOpen] = useState<boolean>(false)

    const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const deleteMutation = useApiDeleteDescription(selectedRow?.id);
    const {mutateAsync: apiDelete} = deleteMutation;

    const handleOpenMovementsTable = (row: IDescription) => {
        setSelectedRow(row);
    }

    const handleConfirmDelete = async () => {
        try {
            if (onSelect) {
                onSelect(undefined);
            }
            await apiDelete({pk: selectedRow?.id});
        } catch (error) {
        }
        setDeleteConfirmationOpen(false);

    };
    const handleCancelDelete = () => {
        setDeleteConfirmationOpen(false);
    };


    const {
        data,
        isLoading,
        isFetching,
    } = useApiGetDescriptions(scope);


    useEffect(() => {
        if (data) {
            setRows(data);
        }
    }, [data]);

    const handleOpenLoadsTable = (row: IDescription) => {
        setSelectedRow(row);
    };


    const CustomCellRenderer: React.FC<{ row: { id: number, text: string }, theme: any }> = ({row, theme}) => {
        const {renderIcons} = useCustomCellRenderer(row, theme);
        return renderIcons();
    };


    const columns = useMemo<MRT_ColumnDef<IDescription>[]>(
        () => {
            const baseColumns: MRT_ColumnDef<IDescription>[] = [
                {
                    id: "icons",
                    header: "ICONS",
                    columnDefType: "display",
                    Cell: ({row}) => (<CustomCellRenderer row={row.original} theme={theme}/>)
                },
                {
                    accessorKey: 'text',
                    header: t('description'),
                },

            ];


            return baseColumns;
        },
        [t]
    );


    const table = useMaterialReactTable<IDescription>({
        columns,
        defaultColumn: {
            maxSize: 400,
            minSize: 80,
            size: 80,
        },
        localization: MRT_Localization_IT,
        data: rows ? rows : [],
        columnFilterDisplayMode: 'popover',
        globalFilterFn: 'contains',
        enableFullScreenToggle: false,
        enableFilters: true,
        enableColumnActions: false,
        enableDensityToggle: false,
        muiTableHeadCellProps: getMuiTableHeadCellProps(theme.palette.primary.main),
        muiTableBodyCellProps: getMuiTableBodyCellProps(theme),
        muiTablePaperProps: getMuiTablePaperProps,
        enablePagination: false,
        initialState: {
            density: 'compact',
            pagination: {pageSize: 25, pageIndex: 0},
        },
        mrtTheme: (theme) => ({
            baseBackgroundColor: theme.palette.backgroundAppGarageColor.main,

        }),
        // note_marco: bg color on row selected
        muiTableBodyRowProps: ({row}) => ({
            ...getMuiTableBodyRowProps(theme),
            // sx: {
            //     backgroundColor: row.original.id === selectedRow?.id ? theme.palette.action.selected : undefined,
            // }
        }),

        enableRowActions: true,
        renderRowActionMenuItems: ({closeMenu, row}) => [
            onSelect && onClose && (
                <MRT_ActionMenuItem
                    icon={<AdsClickIcon color="primary"/>}
                    key="select"
                    label={t("select")}
                    onClick={() => {
                        onSelect(row.original);
                        onClose();
                    }}
                    table={table}
                />),
            <MRT_ActionMenuItem
                icon={<Edit color="primary"/>}
                key="edit"
                label={t("edit")}
                onClick={(event) => {
                    setSelectedRow(row.original);
                    setIsEditFormOpen(true)
                    closeMenu()
                }}
                table={table}
            />,
            <MRT_ActionMenuItem
                icon={<Delete color="warning"/>}
                key="delete"
                label={t("delete")}
                onClick={() => {
                    setSelectedRow(row.original);
                    setDeleteConfirmationOpen(true)
                    closeMenu()
                }}
                table={table}
            />,


        ],
        renderTopToolbarCustomActions: ({table}) => (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <>
                    <Avatar sx={{
                        backgroundColor: 'white',
                        border: `2px solid ${theme.palette.primary.main}`,
                        marginRight: 1
                    }}>
                        <MdOutlineWbIncandescent color={theme.palette.primary.main}/>
                    </Avatar>
                    <MuiReadOnlyBoxBody label={''} value={'description'}/>
                    <MRTMuiButton
                        icon={<AddIcon style={{color: theme.palette.primary.main, fontSize: '2rem'}}/>}
                        onClick={() => {
                            setSelectedRow(undefined);
                            setIsEditFormOpen(true)
                        }} label={'add'}/>
                </>
            </Box>
        )
    });


    return <>
        {isLoading ? <Loading/> :
            <>
                {onClose &&
                    <Grid item xs={12} style={{textAlign: 'center'}}>
                        <Box className={'mt-2 gap-x-2 mb-2'}>
                            <MuiFabButtonBack onClick={onClose}/>
                        </Box>
                    </Grid>
                }


                <MaterialReactTable table={table}/>


                <MuiAlertDialog
                    open={isDeleteConfirmationOpen}
                    title="delete_confirmation"
                    content="ask_for_delete"
                    onClose={handleCancelDelete}
                    onConfirm={handleConfirmDelete}
                />

                <MuiModal
                    top="50%"
                    left="50%"
                    width={'80vh'}
                    open={isEditFormOpen}
                    onClose={() => setIsEditFormOpen(false)}
                >

                    <DescriptionForm
                        scope={scope}
                        objToEdit={selectedRow || null}
                        onClose={() => setIsEditFormOpen(false)}
                    />
                </MuiModal>


                {/*<MuiModal*/}
                {/*    top="50%"*/}
                {/*    left="50%"*/}
                {/*    width={'100vh'}*/}
                {/*    open={isMovementsTableOpen}*/}
                {/*    onClose={() => setIsMovementsTableOpen(false)}*/}
                {/*>*/}
                {/*    {selectedRow &&*/}
                {/*        <WasteMovementsTable my_company_site={my_company_site}*/}
                {/*                             waste_code={selectedRow}*/}
                {/*                             onClose={() => setIsMovementsTableOpen(false)}/>}*/}
                {/*</MuiModal>*/}


                {isFetching &&
                    <MuiFetchingSpinner text="Fetching data..."/>
                }
            </>}
    </>;
};

export default DescriptionTable;
